const BASE_URL = 'https://api.dianziyang.com/api/jingque/admin'

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function onUnauthorized() {
  localStorage.removeItem('auth_token')
  localStorage.removeItem('auth_user')
  window.location.href = '/'
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 401) {
      onUnauthorized()
      throw new Error('未登录或登录已过期')
    }
    if (res.status === 403) throw new Error('无权限访问')
    const text = await res.text()
    throw new Error(text || `请求失败: ${res.status}`)
  }
  return res.json()
}

// ============ Types ============

export interface ChatTemplate {
  id: string
  templateId: string
  name: string
  description: string
  systemPrompt: string
  userPrompt: string
  requiredVariables: string[]
  connectorId: string
  timeoutSeconds: number
  tools: string[]
  maxAgentRounds: number
  sortOrder: number
  status: string
  createdAt: string
  updatedAt: string
}

export interface CreateChatTemplateRequest {
  templateId: string
  name: string
  description?: string
  systemPrompt?: string
  userPrompt: string
  requiredVariables?: string[]
  connectorId?: string
  timeoutSeconds?: number
  tools?: string[]
  maxAgentRounds?: number
}

export interface UpdateChatTemplateRequest {
  name?: string
  description?: string
  systemPrompt?: string
  userPrompt?: string
  requiredVariables?: string[]
  connectorId?: string
  timeoutSeconds?: number
  tools?: string[]
  maxAgentRounds?: number
  status?: string
}

export interface LLMConnector {
  id: string
  connectorId: string
  connectorName: string
  modelName: string
  status: string
}

export interface AnalyzeTemplate {
  id: string
  templateId: string
  name: string
  description: string
  status: string
  itemCount: number
  items: AnalyzeTemplateItem[]
  createdAt: string
  updatedAt: string
}

export interface AnalyzeTemplateItem {
  id: string
  role: string
  chatTemplateId: string
  propertyName: string
  dependsOn: string
  minInputLength: number
  displayOrder: number
}

export interface CreateAnalyzeTemplateRequest {
  templateId: string
  name: string
  description?: string
  items: {
    role: string
    chatTemplateId: string
    propertyName: string
    dependsOn?: string
    minInputLength?: number
    displayOrder?: number
  }[]
}

export interface UpdateAnalyzeTemplateRequest {
  name?: string
  description?: string
  status?: string
  items?: CreateAnalyzeTemplateRequest['items']
}

export interface AnalyzeTask {
  id: string
  taskId: string
  userId: string
  templateId: string
  status: 'pending' | 'running' | 'completed' | 'failed'
  totalItems: number
  successCount: number
  failedCount: number
  errorMessage: string
  variables: Record<string, string>
  results: AnalyzeTaskResult[]
  startedAt: string
  completedAt: string
  createdAt: string
}

export interface AnalyzeTaskResult {
  id: string
  propertyName: string
  result: string
  promptTokens: number
  completionTokens: number
  agentRounds: number
  error: string
}

export interface SystemConfig {
  id: string
  key: string
  value: string
  valueType: string
  description: string
  createdAt: string
  updatedAt: string
}

export interface CreateConfigRequest {
  key: string
  value: string
  valueType: string
  description?: string
}

export interface UpdateConfigRequest {
  value?: string
  description?: string
  changeReason?: string
}

export interface ConfigHistory {
  id: string
  configId: string
  key: string
  oldValue: string
  newValue: string
  changedBy: string
  changeReason: string
  changedAt: string
}

export interface CategoryForest {
  id: string
  code: string
  name: string
  description: string
  sortOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateForestRequest {
  code: string
  name: string
  description?: string
  sourceForestId?: string
}

export interface CategoryTreeNode {
  id: string
  forestId: string
  parentId: string | null
  name: string
  value: string
  nodeType: 'folder' | 'leaf'
  sortOrder: number
  enabled: boolean
  children: CategoryTreeNode[]
  categoryType: CategoryType | null
}

export interface CategoryType {
  id: string
  treeNodeId: string
  name: string
  description: string
  extractionPrompt: string
  sortOrder: number
  enabled: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateCategoryTypeRequest {
  name?: string
  description?: string
  extractionPrompt?: string
  enabled?: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ============ API Methods ============

export const jingqueAdminApi = {
  // ---- Chat Templates ----
  chatTemplates: {
    list: async (page = 1, pageSize = 20): Promise<PaginatedResponse<ChatTemplate>> => {
      const res = await fetch(`${BASE_URL}/chat-templates?page=${page}&pageSize=${pageSize}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateChatTemplateRequest): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateChatTemplateRequest): Promise<ChatTemplate> => {
      const res = await fetch(`${BASE_URL}/chat-templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    batchSortOrder: async (updates: { id: string; sortOrder: number }[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/chat-templates/batch-sort-order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) throw new Error('排序更新失败')
    },
  },

  // ---- LLM Connectors ----
  connectors: {
    list: async (): Promise<{ connectors: LLMConnector[] }> => {
      const res = await fetch(`${BASE_URL}/llm/connectors`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
  },

  // ---- Analyze Templates ----
  analyzeTemplates: {
    list: async (page = 1, pageSize = 20): Promise<PaginatedResponse<AnalyzeTemplate>> => {
      const res = await fetch(`${BASE_URL}/analyze-templates?page=${page}&pageSize=${pageSize}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    get: async (id: string): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    create: async (data: CreateAnalyzeTemplateRequest): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateAnalyzeTemplateRequest): Promise<AnalyzeTemplate> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/analyze-templates/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
  },

  // ---- Analyze Tasks ----
  analyzeTasks: {
    list: async (page = 1, pageSize = 20, userId?: string, status?: string): Promise<PaginatedResponse<AnalyzeTask>> => {
      let url = `${BASE_URL}/analyze-tasks?page=${page}&pageSize=${pageSize}`
      if (userId) url += `&userId=${userId}`
      if (status) url += `&status=${status}`
      const res = await fetch(url, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    get: async (taskId: string): Promise<AnalyzeTask> => {
      const res = await fetch(`${BASE_URL}/analyze-tasks/${taskId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
  },

  // ---- System Configs ----
  configs: {
    list: async (): Promise<{ configs: SystemConfig[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/configs`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    get: async (key: string): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateConfigRequest): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (key: string, data: UpdateConfigRequest): Promise<SystemConfig> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (key: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/configs/${key}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
    getHistory: async (key: string): Promise<{ history: ConfigHistory[]; total: number }> => {
      const res = await fetch(`${BASE_URL}/configs/${key}/history`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
  },

  // ---- Category Forests ----
  categoryForests: {
    list: async (): Promise<{ forests: CategoryForest[] }> => {
      const res = await fetch(`${BASE_URL}/category-forests`, { headers: { ...getAuthHeader() } })
      return handleResponse(res)
    },
    create: async (data: CreateForestRequest): Promise<CategoryForest> => {
      const res = await fetch(`${BASE_URL}/category-forests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
  },

  // ---- Category Tree ----
  categoryTree: {
    get: async (forestId: string): Promise<{ tree: CategoryTreeNode[] }> => {
      const res = await fetch(`${BASE_URL}/category-tree?forestId=${forestId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    createFolder: async (data: { forestId: string; parentId?: string; name: string; sortOrder?: number }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    createLeaf: async (data: {
      forestId: string
      parentId?: string
      name: string
      value?: string
      description?: string
      extractionPrompt?: string
      sortOrder?: number
      enabled?: boolean
    }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree/leaf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    update: async (id: string, data: { name?: string; sortOrder?: number; enabled?: boolean }): Promise<CategoryTreeNode> => {
      const res = await fetch(`${BASE_URL}/category-tree/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
    delete: async (id: string): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeader() },
      })
      if (!res.ok && res.status !== 204) throw new Error('删除失败')
    },
    batchSortOrder: async (updates: { id: string; sortOrder: number }[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-sort-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) throw new Error('排序更新失败')
    },
    batchDelete: async (ids: string[]): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ ids }),
      })
      if (!res.ok) throw new Error('批量删除失败')
    },
    batchEnabled: async (ids: string[], enabled: boolean): Promise<void> => {
      const res = await fetch(`${BASE_URL}/category-tree/batch-enabled`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ ids, enabled }),
      })
      if (!res.ok) throw new Error('批量操作失败')
    },
  },

  // ---- Category Types ----
  categoryTypes: {
    list: async (forestId: string): Promise<{ categoryTypes: CategoryType[] }> => {
      const res = await fetch(`${BASE_URL}/category-types?forestId=${forestId}`, {
        headers: { ...getAuthHeader() },
      })
      return handleResponse(res)
    },
    update: async (id: string, data: UpdateCategoryTypeRequest): Promise<CategoryType> => {
      const res = await fetch(`${BASE_URL}/category-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(data),
      })
      return handleResponse(res)
    },
  },
}
